import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AcceptInviteResponse, BillingSummary, Device, JoinRequestStatus, RegisterSummary, SessionInfo, Tier } from "@shared/api";
import { PLAN_NAMES, TRIAL_WARNING_MS, planPriceLabel } from "@shared/plans";
import type { RegisterSettings } from "@shared/events";
import { DEFAULT_SETTINGS } from "@shared/ledger";
import type { CurrencyCode } from "@shared/money";
import { formatMinor } from "@shared/money";
import { Badge, BigButton, TabBar, TextField } from "@client/ui";
import type { TabItem } from "@client/ui";
import { ApiRequestError, SessionProvider, apiFetch, hasCachedSession, setDeviceId, signOut, useSession } from "@client/auth";
import { RegisterProvider, applyTheme, resolveDeps, useRegister } from "@client/state";
import type { AppDeps } from "@client/state";
import type { LocalRegister } from "@client/db/contract";
import { checkRegisterAccess } from "./sharedRegisterAccess";
import { capturePendingInvite, clearPendingInvite, getPendingInvite } from "./pendingInvite";
import { formatCountdown } from "./countdown";
import { takeBillingReturn } from "./billingReturn";
import { lazyComponent, type LazyStates } from "./lazy";
import type { SetupSection } from "./screens/Setup";
import { SellScreen } from "./screens/Sell";
import { SellSummaryContext, SellSummaryDisplay, type SellSummary } from "./screens/Sell/summary";
import { PwaBanners } from "./PwaBanners";
import "./styles/tokens.css";
import "./styles/ui.css";
import "./styles/app.css";

/**
 * OWNED BY C1. The app shell: auth gate → register picker → register.
 * Register name and sync status are always on screen; the header's top-right slot shows the running sale
 * total while selling and is otherwise empty — cash on hand lives on the Drawer screen only (D-051).
 */

export type Tab = "sell" | "history" | "drawer" | "setup";

const TABS: ReadonlyArray<TabItem<Tab>> = [
  { id: "sell", label: "Sell", glyph: "▦" },
  { id: "history", label: "History", glyph: "☰" },
  { id: "drawer", label: "Drawer", glyph: "▤" },
  { id: "setup", label: "Setup", glyph: "⚙" },
];

const ACTIVE_KEY = "wc.activeRegister";

/*
 * D-056: the main bundle holds what a signed-in register needs for its first paint (the shell and the Sell
 * screen). Everything else is its own chunk: the sign-in screen with the Better Auth client, the landing
 * page's pitch (D-055), the register picker and the other three tabs. The shell preloads the tabs as soon
 * as it is up, and a device with no remembered session preloads the sign-in screen while /api/me is still
 * in flight, so in practice none of them ever shows its loading state.
 */
const TAB_STATES: LazyStates = {
  loading: <TabLoading />,
  failed: (retry) => <LoadFailed onRetry={retry} />,
};
const PAGE_STATES: LazyStates = {
  loading: <Splash sub={null} />,
  failed: (retry) => <LoadFailed onRetry={retry} page />,
};
const AuthScreen = lazyComponent(() => import("./auth/AuthScreen").then((m) => m.AuthScreen), PAGE_STATES);
// No loading or failed state: without its pitch the sign-in form simply stands alone.
const LandingPitch = lazyComponent(() => import("./auth/LandingPitch").then((m) => m.LandingPitch));
const RegisterPicker = lazyComponent(() => import("./screens/Registers").then((m) => m.RegisterPicker), PAGE_STATES);
const HistoryScreen = lazyComponent(() => import("./screens/History").then((m) => m.HistoryScreen), TAB_STATES);
const DrawerScreen = lazyComponent(() => import("./screens/Drawer").then((m) => m.DrawerScreen), TAB_STATES);
const SetupScreen = lazyComponent(() => import("./screens/Setup").then((m) => m.SetupScreen), TAB_STATES);
// D-071: the plan picker a new account lands on is Setup → Plan's own section, from its own small chunk.
const PlanChooser = lazyComponent(() => import("./screens/Setup/Billing").then((m) => m.BillingSection), PAGE_STATES);
// D-077: the owner's approval popup, fetched only when a device is actually waiting.
const JoinRequestPrompt = lazyComponent(() => import("./JoinRequestPrompt").then((m) => m.JoinRequestPrompt));

const signOutAndReload = () => void signOut().then(() => location.reload());

export function App({ deps }: { deps?: AppDeps } = {}) {
  // Capture a `/join#<token>` invite link before anything renders, so it survives whatever the invitee
  // does next (sign up, sign in, or a full-page Google OAuth redirect) — see pendingInvite.ts. Idempotent
  // and cheap once the URL is no longer /join, so calling it on every render is harmless.
  capturePendingInvite();
  return (
    <SessionProvider>
      <AuthGate deps={deps} />
    </SessionProvider>
  );
}

function AuthGate({ deps }: { deps?: AppDeps }) {
  const { state, refresh } = useSession();
  // D-055/D-056: a device with no remembered session is probably about to see the landing page, so fetch the
  // sign-in screen and its pitch while /api/me is in flight. A device that was signed in only fetches them
  // once its session turns out to have ended.
  const landingLikely = state.status === "signed-out" || (state.status === "loading" && !hasCachedSession());
  useEffect(() => {
    if (!landingLikely) return;
    void AuthScreen.preload();
    void LandingPitch.preload();
  }, [landingLikely]);
  // D-052, D-072: returning from Stripe Checkout. Our session is KV-cached for 60 s and the webhook cannot
  // drop that cache (it has no cookie), and the webhook itself may still be on its way. So read the plan
  // from Stripe through "Restore purchase", which also drops the cache, before showing anything: otherwise a
  // new account would be shown the plan picker it just paid to leave. Read in a lazy initializer so the flag
  // is taken off the URL exactly once, before the effect can run twice in React 19 dev.
  const [billingReturn] = useState(takeBillingReturn);
  const [confirming, setConfirming] = useState(billingReturn === "success");
  useEffect(() => {
    if (billingReturn === "managed") void refresh();
    if (billingReturn !== "success") return;
    let live = true;
    void confirmCheckout().finally(async () => {
      await refresh();
      if (live) setConfirming(false);
    });
    return () => { live = false; };
  }, [billingReturn, refresh]);
  const onPlanChanged = useCallback(() => void refresh(), [refresh]);

  if (state.status === "loading") return <Splash />;
  if (confirming && state.status === "signed-in") return <Splash sub="Starting your plan…" />;
  if (state.status === "signed-out") {
    return <AuthScreen onSignedIn={() => void refresh()} pitch={<LandingPitch />} />;
  }
  if (state.session === null) {
    return (
      <div className="wc-splash">
        <h1 className="wc-splash__title">WebCashier</h1>
        <p>You are offline and this device has no cached sign-in. Connect once to get started.</p>
      </div>
    );
  }
  return <RegisterHost session={state.session} deps={deps} offline={state.status === "offline"} onPlanChanged={onPlanChanged} />;
}

/**
 * D-072: after Checkout, ask the Worker to read the new subscription from Stripe (D-062's restore). The
 * webhook usually lands first; when it has not, the restore links the account itself. Stripe's search index
 * can lag a brand-new subscription, so a 404 is retried a few times before giving up quietly.
 */
async function confirmCheckout(): Promise<void> {
  for (let attempt = 0; attempt < 4; attempt += 1) {
    try {
      const next = await apiFetch<BillingSummary>("/api/billing/restore", { method: "POST" });
      if (next.tier !== "none") return;
    } catch {
      /* not found yet, or offline: try again */
    }
    await new Promise((resolve) => setTimeout(resolve, 2500));
  }
}

/**
 * D-071: an account with no plan and no register to open, which is every new account: choose a plan and
 * start the free trial. A user who only joins other people's registers as a cashier never needs one.
 */
function ChoosePlanPage({ tier, notice, onPlanChanged, onSignOut, onBack }: {
  tier: Tier;
  /** D-070: e.g. that a shared register was just removed from this device. */
  notice?: string | null;
  onPlanChanged?: () => void;
  onSignOut: () => void;
  /** Present when the account has registers to go back to (as a cashier). */
  onBack?: () => void;
}) {
  return (
    <div className="wc-auth">
      <header className="wc-auth__brand">
        <p className="wc-auth__eyebrow">WebCashier</p>
        <h1 className="wc-auth__title">Choose your plan</h1>
        <p className="wc-auth__lede">Lite or Pro, monthly or yearly. Either one starts with a 7-day free trial.</p>
      </header>
      <div className="wc-auth__card">
        {notice ? <p className="wc-banner" role="status">{notice}</p> : null}
        <PlanChooser tier={tier} onPlanChanged={onPlanChanged} />
        {onBack ? <BigButton variant="secondary" block onClick={onBack}>Back to registers</BigButton> : null}
        <BigButton variant="ghost" block onClick={onSignOut}>Sign out</BigButton>
      </div>
    </div>
  );
}

function Splash({ sub = "Opening your register…" }: { sub?: string | null }) {
  return (
    <div className="wc-splash">
      <h1 className="wc-splash__title">WebCashier</h1>
      {sub ? <p className="wc-splash__sub">{sub}</p> : null}
    </div>
  );
}

/** A code-split tab whose chunk has not arrived yet (D-056). The shell preloads them, so this is rare. */
function TabLoading() {
  return (
    <section className="wc-screen" aria-busy="true">
      <p className="wc-hint">Loading…</p>
    </section>
  );
}

/** A code-split screen whose chunk could not be fetched, e.g. offline before the service worker cached it. */
function LoadFailed({ onRetry, page = false }: { onRetry: () => void; page?: boolean }) {
  return (
    <section className={page ? "wc-splash" : "wc-screen"} role="alert">
      <p className="wc-hint">This screen did not load. Check the connection, then try again.</p>
      <BigButton variant="secondary" onClick={onRetry}>Try again</BigButton>
    </section>
  );
}

function RegisterHost({ session, deps: injected, offline, onPlanChanged }: {
  session: SessionInfo;
  deps?: AppDeps;
  offline: boolean;
  /** Re-reads the session after Setup → Plan changes the tier (D-062). */
  onPlanChanged?: () => void;
}) {
  const [deps, setDeps] = useState<AppDeps | null>(injected ?? null);
  const [registers, setRegisters] = useState<RegisterSummary[] | null>(null);
  const [activeId, setActiveId] = useState<string | null>(() => {
    try { return localStorage.getItem(ACTIVE_KEY); } catch { return null; }
  });
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joinToken, setJoinToken] = useState<string | null>(() => getPendingInvite());
  // D-070: what this device knows about a shared register the server no longer serves it.
  const [locals, setLocals] = useState<LocalRegister[]>([]);
  const [checking, setChecking] = useState<ReadonlySet<string>>(() => new Set());
  const [readOnlyId, setReadOnlyId] = useState<string | null>(null);
  /** `ended`: removed because this device's cashier session ran out (D-081), not because the owner took it away. */
  const [removals, setRemovals] = useState<ReadonlyArray<{ id: string; name: string | null; ended: boolean }>>([]);
  const [notice, setNotice] = useState<string | null>(null);
  /** D-071: an account with no plan that holds other people's registers can still go and choose one. */
  const [choosingPlan, setChoosingPlan] = useState(false);
  /** Bumped to remount the register, which restarts a sync client that stopped on `revoked`. */
  const [epoch, setEpoch] = useState(0);
  const registersRef = useRef<RegisterSummary[]>([]);

  useEffect(() => {
    if (injected) { setDeps(injected); return; }
    let live = true;
    void resolveDeps().then((d) => { if (live) setDeps(d); });
    return () => { live = false; };
  }, [injected]);

  useEffect(() => {
    if (!deps) return;
    void deps.store.getMeta().then((m) => setDeviceId(m.deviceId));
  }, [deps]);

  /**
   * D-070: ask the server (fresh from D1) whether this device still has a shared register, and act on it:
   * read-only while the owner is not on Pro, removed from the device once the owner takes it away. `onUnknown`
   * says what to do when the server cannot be asked.
   */
  const settleAccess = useCallback(async (id: string, opts: { onUnknown: "keep" | "readOnly"; restartSync?: boolean }) => {
    if (!deps) return;
    setChecking((prev) => new Set(prev).add(id));
    try {
      const { outcome, name } = await checkRegisterAccess(id, deps.store);
      if (outcome === "remove" || outcome === "ended") {
        setRemovals((prev) => (prev.some((r) => r.id === id) ? prev : [...prev, { id, name, ended: outcome === "ended" }]));
      } else if (outcome === "readOnly" || (outcome === "unknown" && opts.onUnknown === "readOnly")) {
        setReadOnlyId(id);
      } else if (outcome === "normal") {
        setReadOnlyId((current) => (current === id ? null : current));
        if (opts.restartSync) setEpoch((n) => n + 1);
      }
    } finally {
      setChecking((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, [deps]);

  const loadRegisters = useCallback(async (): Promise<RegisterSummary[]> => {
    try {
      const list = await apiFetch<RegisterSummary[]>("/api/registers");
      registersRef.current = list;
      setRegisters(list);
      // A shared register this device holds that the server no longer lists was suspended or taken away:
      // keep it in place until the server says which (D-070).
      const held = deps ? await deps.store.localRegisters() : [];
      setLocals(held);
      const dropped = held.filter((r) => r.role === "cashier" && !list.some((s) => s.id === r.id));
      setActiveId((current) =>
        current && (list.some((r) => r.id === current) || dropped.some((r) => r.id === current)) ? current : list[0]?.id ?? null,
      );
      for (const r of dropped) void settleAccess(r.id, { onUnknown: "readOnly" });
      return list;
    } catch {
      // Offline or the API is not up yet: fall back to whatever this device already has (F-106).
      const meta = deps ? await deps.store.getMeta() : null;
      if (deps) setLocals(await deps.store.localRegisters());
      setRegisters([]);
      setActiveId((current) => current ?? meta?.activeRegisterId ?? null);
      return [];
    }
  }, [deps, settleAccess]);

  useEffect(() => { if (deps) void loadRegisters(); }, [deps, loadRegisters]);

  const active = useMemo(() => registers?.find((r) => r.id === activeId) ?? null, [registers, activeId]);
  // D-071: Pro makes as many registers as it likes, Lite one of its own, and an account with no plan none.
  const ownedCount = session.grants.filter((g) => g.role === "owner").length;
  const canCreate = session.tier === "pro" || (session.tier === "lite" && ownedCount === 0);
  const local = locals.find((r) => r.id === activeId) ?? null;
  // A shared register that dropped off the list is no longer in the session's grants, so its role comes
  // from what this device remembered. Never default a remembered cashier register to owner (D-070).
  const role = session.grants.find((g) => g.registerId === activeId)?.role ?? local?.role ?? "owner";

  const choose = useCallback((id: string) => {
    setActiveId(id);
    setPicking(false);
    setNotice(null);
    try { localStorage.setItem(ACTIVE_KEY, id); } catch { /* private mode */ }
    const settings = registers?.find((r) => r.id === id)?.settings;
    const pickedRole = session.grants.find((g) => g.registerId === id)?.role;
    if (deps && settings) void deps.store.setActiveRegister(id, settings, pickedRole);
  }, [deps, registers, session.grants]);

  // Remember this device's role and the latest settings for the active register, so they are known locally
  // if the server later stops listing it (D-070).
  const grantRole = session.grants.find((g) => g.registerId === active?.id)?.role;
  useEffect(() => {
    if (!deps || !active || !grantRole) return;
    void deps.store.setActiveRegister(active.id, active.settings, grantRole);
  }, [deps, active, grantRole]);

  // D-073: an owner's sync is refused once the account has no plan. Re-read the session to find out, at most
  // once a minute, so the register turns read-only instead of piling up sales that cannot upload.
  const lastPlanCheck = useRef(0);
  useEffect(() => {
    if (!deps?.sync || !activeId || role !== "owner" || !onPlanChanged) return;
    return deps.sync.subscribe((status) => {
      if (status !== "revoked" && status !== "error") return;
      const now = Date.now();
      if (now - lastPlanCheck.current < 60_000) return;
      lastPlanCheck.current = now;
      onPlanChanged();
    });
  }, [deps, activeId, role, onPlanChanged]);

  // A cashier device whose sync is refused finds out why, straight away (D-070).
  useEffect(() => {
    if (!deps?.sync || !activeId || role !== "cashier") return;
    return deps.sync.subscribe((status) => {
      if (status === "revoked") void settleAccess(activeId, { onUnknown: "keep", restartSync: true });
      else if (status === "error") void settleAccess(activeId, { onUnknown: "keep" });
    });
  }, [deps, activeId, role, settleAccess]);

  // D-081: a timed cashier session ends at a set time. Stop recording then, even offline, and ask the server,
  // which removes the register from the device. The router and the Register DO enforce the end as well.
  const sessionEndsAt = role === "cashier" ? session.grants.find((g) => g.registerId === activeId)?.sessionEndsAt ?? null : null;
  useEffect(() => {
    if (!activeId || sessionEndsAt === null) return;
    const end = () => {
      setReadOnlyId(activeId);
      void settleAccess(activeId, { onUnknown: "readOnly" });
    };
    const wait = sessionEndsAt - Date.now();
    if (wait <= 0) { end(); return; }
    if (wait > MAX_TIMER_MS) return;
    const timer = setTimeout(end, wait);
    return () => clearTimeout(timer);
  }, [activeId, sessionEndsAt, settleAccess]);

  // Clearing a removed register runs once its RegisterProvider has unmounted (and stopped syncing), one at a
  // time. Wiping is idempotent, so a run cut short by a re-render is simply repeated.
  const removal = removals[0] ?? null;
  useEffect(() => {
    if (!removal || !deps) return;
    let live = true;
    void (async () => {
      await deps.store.wipeRegister(removal.id);
      try { if (localStorage.getItem(ACTIVE_KEY) === removal.id) localStorage.removeItem(ACTIVE_KEY); } catch { /* private mode */ }
      if (!live) return;
      setLocals((prev) => prev.filter((r) => r.id !== removal.id));
      // The list was fetched before the removal; the server stops listing the register for this device.
      registersRef.current = registersRef.current.filter((r) => r.id !== removal.id);
      setRegisters((prev) => prev?.filter((r) => r.id !== removal.id) ?? prev);
      setReadOnlyId((current) => (current === removal.id ? null : current));
      setActiveId((current) => (current === removal.id ? registersRef.current.find((r) => r.id !== removal.id)?.id ?? null : current));
      const named = removal.name ? `“${removal.name}”` : "a shared register";
      setNotice(removal.ended
        ? `Your cashier session on ${named} ended, so it was removed from this device. Scan the invite again to rejoin.`
        : `${named.charAt(0).toUpperCase()}${named.slice(1)} was removed from this device because its owner stopped sharing it with you.`);
      setRemovals((prev) => prev.filter((r) => r.id !== removal.id));
    })();
    return () => { live = false; };
    // Keyed on the id so a second removal joining the queue does not restart the first.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removal?.id, deps]);

  if (!deps || registers === null) return <Splash />;
  if (removal) return <Splash sub="Removing a register this device no longer has…" />;
  if (activeId && checking.has(activeId) && !active) return <Splash sub="Checking access to this register…" />;

  if (joinToken) {
    return (
      <JoinInvite
        token={joinToken}
        onDone={(device) => {
          clearPendingInvite();
          setJoinToken(null);
          if (!device) return;
          // Re-read the session so its grants carry the register just joined. Until then this device takes
          // itself for the register's owner, which an invitee with no plan of their own sees as frozen (D-073).
          onPlanChanged?.();
          void loadRegisters().then((list) => {
            setActiveId(device.registerId);
            try { localStorage.setItem(ACTIVE_KEY, device.registerId); } catch { /* private mode */ }
            const settings = list.find((r) => r.id === device.registerId)?.settings;
            if (settings) void deps.store.setActiveRegister(device.registerId, settings, "cashier");
          });
        }}
      />
    );
  }

  const needsPicker = picking || activeId === null;
  if (choosingPlan || (needsPicker && session.tier === "none" && registers.length === 0)) {
    return (
      <ChoosePlanPage
        tier={session.tier}
        notice={notice}
        onPlanChanged={onPlanChanged}
        onSignOut={signOutAndReload}
        onBack={registers.length > 0 ? () => setChoosingPlan(false) : undefined}
      />
    );
  }
  if (needsPicker) {
    return (
      <RegisterPicker
        registers={registers}
        activeId={activeId}
        canCreate={canCreate}
        onChoosePlan={session.tier === "none" ? () => setChoosingPlan(true) : undefined}
        busy={busy}
        error={error}
        notice={notice}
        onPick={choose}
        onSignOut={signOutAndReload}
        onCreate={async ({ name, currency }) => {
          setBusy(true);
          setError(null);
          try {
            const created = await apiFetch<RegisterSummary>("/api/registers", { method: "POST", json: { name, currency } });
            setRegisters((prev) => [...(prev ?? []), created]);
            choose(created.id);
          } catch (e) {
            setError(e instanceof Error ? e.message : "Could not create the register.");
          } finally {
            setBusy(false);
          }
        }}
      />
    );
  }

  const settings: RegisterSettings = active?.settings ?? local?.settings ?? { ...DEFAULT_SETTINGS, name: "My Register" };
  // D-073: the owner of an account with no plan keeps their registers, frozen read-only.
  const frozen = session.tier === "none" && role === "owner";
  const readOnly = frozen || readOnlyId === activeId;

  return (
    <RegisterProvider
      key={`${activeId}:${epoch}`}
      registerId={activeId}
      settings={settings}
      store={deps.store}
      sync={deps.sync}
      backup={deps.backup}
      role={role}
      tier={session.tier}
      readOnly={readOnly}
    >
      <Shell
        tier={session.tier}
        frozen={frozen}
        sessionEndsAt={sessionEndsAt}
        trialEndsAt={role === "owner" ? session.trialEndsAt : null}
        offline={offline}
        durable={deps.durable}
        onSwitchRegister={registers.length > 1 || canCreate ? () => setPicking(true) : undefined}
        onSignOut={signOutAndReload}
        onPlanChanged={onPlanChanged}
        onCheckAccess={readOnly && !frozen ? () => void settleAccess(activeId, { onUnknown: "keep", restartSync: true }) : undefined}
      />
    </RegisterProvider>
  );
}

export function Shell({ tier, frozen = false, sessionEndsAt = null, trialEndsAt = null, offline, durable = true, onSwitchRegister, onSignOut, onPlanChanged, onCheckAccess }: {
  tier: Tier;
  /** D-073: this is the owner's own register and the account has no plan. */
  frozen?: boolean;
  /** D-081: when this cashier device's session on the register ends; null for no limit. */
  sessionEndsAt?: number | null;
  /** D-072: when the owner's free trial ends, for the banner in its last 48 hours. */
  trialEndsAt?: number | null;
  offline?: boolean;
  durable?: boolean;
  onSwitchRegister?: () => void;
  onSignOut?: () => void;
  onPlanChanged?: () => void;
  /** D-070: shown on a read-only device, to look again once the owner may have upgraded. */
  onCheckAccess?: () => void;
}) {
  const { state, settings, syncStatus, pendingCount, ready, readOnly, joinRequests } = useRegister();
  // A read-only device opens on History: there is nothing to sell (D-070).
  const [tab, setTab] = useState<Tab>(() => (readOnly ? "history" : "sell"));
  // The Sell screen publishes its running total here; the header shows it. SellScreen clears it on unmount,
  // so it is only ever populated while the Sell tab is open (D-051).
  const [sellSummary, setSellSummary] = useState<SellSummary | null>(null);
  // The banners open Setup straight on Plan. Keyed so that a second tap reopens it from the hub.
  const [setupEntry, setSetupEntry] = useState<{ key: number; section: SetupSection | null }>({ key: 0, section: null });
  const openPlan = useCallback(() => {
    setSetupEntry((prev) => ({ key: prev.key + 1, section: "billing" }));
    setTab("setup");
  }, []);

  useEffect(() => { applyTheme(settings.theme); }, [settings.theme]);
  // D-056: the other tabs, and the register picker behind "Switch register", are separate chunks. Fetch them
  // once the register is on screen, so the first tap on a tab never waits for the network.
  useEffect(() => {
    void HistoryScreen.preload();
    void DrawerScreen.preload();
    void SetupScreen.preload();
    void RegisterPicker.preload();
  }, []);

  return (
    <div className="wc-app">
      <Header tier={tier} offline={offline} sellSummary={sellSummary} />
      <PwaBanners />
      {trialEndsAt !== null ? <TrialBanner trialEndsAt={trialEndsAt} onOpenPlan={openPlan} /> : null}
      {frozen ? <PlanEndedBanner pendingCount={pendingCount} onChoosePlan={openPlan} />
        : readOnly && sessionEndsAt !== null && sessionEndsAt <= Date.now() ? <SessionEndedBanner pendingCount={pendingCount} onCheckAccess={onCheckAccess} />
        : readOnly ? <ReadOnlyBanner pendingCount={pendingCount} onCheckAccess={onCheckAccess} />
        : sessionEndsAt !== null ? <SessionEndingBanner endsAt={sessionEndsAt} /> : null}
      {!durable ? (
        <p className="wc-banner wc-banner--warn" role="status">
          Local storage is not available in this build — sales will not survive a reload.
        </p>
      ) : null}
      <SellSummaryContext.Provider value={setSellSummary}>
        <main className="wc-app__main" aria-busy={!ready}>
          {tab === "sell" && (readOnly ? <ReadOnlySell frozen={frozen} /> : <SellScreen />)}
          {tab === "history" && <HistoryScreen />}
          {tab === "drawer" && <DrawerScreen />}
          {tab === "setup" && (
            <SetupScreen
              key={setupEntry.key}
              initialSection={setupEntry.section}
              onSignOut={onSignOut}
              onSwitchRegister={onSwitchRegister}
              onPlanChanged={onPlanChanged}
            />
          )}
        </main>
      </SellSummaryContext.Provider>
      <TabBar items={TABS} active={tab} onChange={setTab} />
      {tier === "pro" && joinRequests.length > 0 ? <JoinRequestPrompt requests={joinRequests} /> : null}
      <span className="wc-visually-hidden" role="status">
        {state.day ? `Cash on hand ${formatMinor(state.cashOnHandMinor, settings.currency)}` : "Drawer closed"}
        {pendingCount > 0 ? `, ${pendingCount} events waiting` : ""}
        {` · sync ${syncStatus}`}
      </span>
    </div>
  );
}

function Header({ tier, offline, sellSummary }: { tier: Tier; offline?: boolean; sellSummary?: SellSummary | null }) {
  const { settings, syncStatus, pendingCount, readOnly } = useRegister();
  const currency: CurrencyCode = settings.currency;
  return (
    <header className="wc-header">
      <div className="wc-header__id">
        <p className="wc-header__name">{settings.name}</p>
        {settings.headerText && tier === "pro" ? <p className="wc-header__text">{settings.headerText}</p> : null}
      </div>
      <div className="wc-header__cash">
        {sellSummary ? <SellSummaryDisplay summary={sellSummary} currency={currency} /> : null}
        <SyncBadge status={readOnly ? "readOnly" : offline ? "offline" : syncStatus} pending={pendingCount} tier={tier} />
      </div>
    </header>
  );
}

export function SyncBadge({ status, pending, tier }: { status: string; pending: number; tier: Tier }) {
  const tone =
    status === "readOnly" ? "warn"
    : status === "online" ? (pending > 0 ? "warn" : "ok")
    : status === "revoked" || status === "error" ? "danger"
    : "neutral";
  const text =
    status === "readOnly" ? "Read-only"
    : status === "revoked" ? "Access revoked"
    : status === "error" ? "Sync problem"
    : pending > 0 ? `${pending} to upload`
    : status === "online" ? "Backed up"
    : status === "connecting" ? "Connecting"
    : "Offline";
  const title = tier === "none"
    ? "This account has no plan, so nothing new is backed up. Export any time from Setup → Data."
    : "Backed up while your subscription is active.";
  return (
    <span title={title}>
      <Badge tone={tone as "ok" | "warn" | "danger" | "neutral"} dot>{text}</Badge>
    </span>
  );
}

/**
 * D-070: a cashier device whose register owner is not on Pro, the plan with cashier devices. Nothing is
 * recorded or synced; what was not uploaded stays on the device and goes up if the owner returns to Pro.
 */
function ReadOnlyBanner({ pendingCount, onCheckAccess }: { pendingCount: number; onCheckAccess?: () => void }) {
  return (
    <div className="wc-banner wc-banner--warn" role="status">
      <p style={{ margin: 0 }}>
        Read-only. This register's owner is not on the Pro plan, which is the one with cashier devices, so this
        device can't record sales.
        {pendingCount > 0
          ? ` ${pendingCount} change${pendingCount === 1 ? "" : "s"} not yet uploaded will stay on this device and upload if the owner returns to Pro.`
          : ""}
      </p>
      {onCheckAccess ? <BigButton variant="secondary" onClick={onCheckAccess}>Check again</BigButton> : null}
    </div>
  );
}

/** D-081: how long before a timed cashier session ends the device starts warning about it. */
const SESSION_WARNING_MS = 15 * 60_000;
/** Longer than this and setTimeout fires at once instead (it holds a signed 32-bit delay). */
const MAX_TIMER_MS = 2 ** 31 - 1;

/** D-081: the last 15 minutes of a timed cashier session. Shows itself when that window starts. */
function SessionEndingBanner({ endsAt }: { endsAt: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const wait = endsAt - SESSION_WARNING_MS - Date.now();
    if (wait <= 0 || wait > MAX_TIMER_MS) return;
    const timer = setTimeout(() => setNow(Date.now()), wait);
    return () => clearTimeout(timer);
  }, [endsAt]);
  if (now < endsAt - SESSION_WARNING_MS || now >= endsAt) return null;
  return (
    <p className="wc-banner wc-banner--warn" role="status">
      Your cashier session ends at {new Date(endsAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}. Finish
      the sale in progress before then.
    </p>
  );
}

/** D-081: a timed cashier session that has run out, on a device that could not yet be told by the server. */
function SessionEndedBanner({ pendingCount, onCheckAccess }: { pendingCount: number; onCheckAccess?: () => void }) {
  return (
    <div className="wc-banner wc-banner--warn" role="status">
      <p style={{ margin: 0 }}>
        Your cashier session on this register has ended, so this device can't record sales. The register is taken
        off this device once it is back online. Scan the invite again to rejoin.
        {pendingCount > 0
          ? ` ${pendingCount} change${pendingCount === 1 ? "" : "s"} recorded offline could not be uploaded before the session ended.`
          : ""}
      </p>
      {onCheckAccess ? <BigButton variant="secondary" onClick={onCheckAccess}>Check again</BigButton> : null}
    </div>
  );
}

/**
 * D-073: the owner's own register on an account with no plan (a free trial that did not convert, or a
 * subscription that lapsed). Everything recorded is kept, nothing new is, until a plan starts again.
 */
function PlanEndedBanner({ pendingCount, onChoosePlan }: { pendingCount: number; onChoosePlan: () => void }) {
  return (
    <div className="wc-banner wc-banner--warn" role="status">
      <span>
        Read-only. This account has no active plan, so nothing new can be recorded. Everything you recorded is kept.
        {pendingCount > 0
          ? ` ${pendingCount} change${pendingCount === 1 ? "" : "s"} not yet uploaded will stay on this device and upload once you choose a plan.`
          : ""}
      </span>
      <div className="wc-banner__actions">
        <button type="button" className="wc-banner__btn" onClick={onChoosePlan}>Choose a plan</button>
      </div>
    </div>
  );
}

/** D-072: the trial's last 48 hours. Small, not dismissible, and gone once the trial converts or ends. */
export function TrialBanner({ trialEndsAt, onOpenPlan, now: nowOverride }: {
  trialEndsAt: number;
  onOpenPlan: () => void;
  /** Injected in tests. */
  now?: number;
}) {
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => {
    if (nowOverride !== undefined) return;
    const t = setInterval(() => setClock(Date.now()), 60_000);
    return () => clearInterval(t);
  }, [nowOverride]);
  const now = nowOverride ?? clock;
  const left = trialEndsAt - now;
  const show = left > 0 && left <= TRIAL_WARNING_MS;

  // Whether the trial will convert, and to what, is on Stripe's side: ask once, only inside the window.
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  useEffect(() => {
    if (!show) return;
    let live = true;
    apiFetch<BillingSummary>("/api/billing").then((s) => { if (live) setSummary(s); }, () => { /* keep the generic copy */ });
    return () => { live = false; };
  }, [show]);
  if (!show) return null;

  const hours = Math.round(left / 3_600_000);
  const minutes = Math.max(1, Math.round(left / 60_000));
  const inWords = left < 3_600_000
    ? `${minutes} minute${minutes === 1 ? "" : "s"}`
    : `${hours} hour${hours === 1 ? "" : "s"}`;
  const at = new Date(trialEndsAt).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" });
  const then = summary?.cancelAt
    ? "You cancelled, so nothing is charged and your registers become read-only then."
    : summary?.planTier && summary.interval
      ? `Then ${PLAN_NAMES[summary.planTier]} at ${planPriceLabel(summary.planTier, summary.interval)} is charged to your card unless you cancel.`
      : "Then your card is charged unless you cancel.";
  return (
    <div className="wc-banner wc-banner--warn wc-banner--row" role="status">
      <span>Your free trial ends in {inWords} ({at}). {then}</span>
      <button type="button" className="wc-banner__btn wc-banner__btn--quiet" onClick={onOpenPlan}>Plan</button>
    </div>
  );
}

/** D-070, D-073: the Sell tab on a read-only device. */
function ReadOnlySell({ frozen = false }: { frozen?: boolean }) {
  return (
    <section className="wc-screen">
      <h1 className="wc-screen__title">Sell</h1>
      <p className="wc-hint">
        {frozen
          ? "Selling is paused until this account has a plan again. History and Drawer still show what was recorded."
          : "Selling is paused on this device. History and Drawer still show what was recorded."}
      </p>
    </section>
  );
}

/** D-077: how often a device waiting for the owner's approval asks whether they have decided. */
const JOIN_POLL_MS = 3_000;

/** F-801: completes a `/join#<token>` invite once the invitee is signed in. Server side (POST
 *  /api/invites/accept) already existed and is idempotent; this screen was the missing piece. An invite that
 *  needs the owner's approval (D-077) answers with a join request instead, and the screen waits on it. */
export function JoinInvite({ token, onDone, pollMs = JOIN_POLL_MS }: {
  token: string;
  onDone: (device: Device | null) => void;
  pollMs?: number;
}) {
  const [deviceName, setDeviceName] = useState(guessDeviceName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [request, setRequest] = useState<JoinRequestStatus | null>(null);

  const join = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch<AcceptInviteResponse>("/api/invites/accept", { method: "POST", json: { token, deviceName } });
      if ("joinRequest" in res) setRequest(res.joinRequest);
      else onDone(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not join this register.");
    } finally {
      setBusy(false);
    }
  }, [token, deviceName, onDone]);

  if (request) {
    return (
      <JoinWaiting
        request={request}
        pollMs={pollMs}
        busy={busy}
        error={error}
        onChange={setRequest}
        onAskAgain={() => void join()}
        onDone={onDone}
      />
    );
  }

  return (
    <div className="wc-auth">
      <header className="wc-auth__brand">
        <p className="wc-auth__eyebrow">WebCashier</p>
        <h1 className="wc-auth__title">Join a register</h1>
        <p className="wc-auth__lede">Name this device so the owner recognizes it in their device list.</p>
      </header>
      <div className="wc-auth__card">
        <TextField label="Device name" value={deviceName} onChange={setDeviceName} maxGraphemes={40} />
        {error ? <p className="wc-auth__notice wc-auth__notice--error" role="alert">{error}</p> : null}
        <BigButton
          variant="primary"
          size="lg"
          block
          disabled={busy || deviceName.trim().length === 0}
          onClick={() => void join()}
        >
          {busy ? "Joining…" : "Join"}
        </BigButton>
        <BigButton variant="ghost" block disabled={busy} onClick={() => onDone(null)}>
          Not now
        </BigButton>
      </div>
    </div>
  );
}

/** D-077: the invitee's side of an approval invite, from "waiting" to the owner's answer. */
function JoinWaiting({ request, pollMs, busy, error, onChange, onAskAgain, onDone }: {
  request: JoinRequestStatus;
  pollMs: number;
  busy: boolean;
  error: string | null;
  onChange: (next: JoinRequestStatus) => void;
  onAskAgain: () => void;
  onDone: (device: Device | null) => void;
}) {
  const [now, setNow] = useState(() => Date.now());
  const [leaving, setLeaving] = useState(false);
  const pending = request.status === "pending" && request.expiresAt > now;
  // The parent's callbacks may change on every render; the poll below must not restart because of it.
  const callbacks = useRef({ onChange, onDone });
  useEffect(() => { callbacks.current = { onChange, onDone }; });

  // Ask until the owner decides or the request runs out. A failed poll (offline, a blip) just tries again.
  useEffect(() => {
    if (request.status !== "pending") return;
    let live = true;
    const timer = setTimeout(() => {
      void apiFetch<JoinRequestStatus>(`/api/join-requests/${request.id}/status`).then(
        (next) => {
          if (!live) return;
          if (next.status === "approved" && next.device) callbacks.current.onDone(next.device);
          else callbacks.current.onChange(next);
        },
        (e: unknown) => {
          if (!live) return;
          if (e instanceof ApiRequestError && e.status === 404) callbacks.current.onChange({ ...request, status: "cancelled" });
          else callbacks.current.onChange({ ...request });
        },
      );
    }, pollMs);
    return () => { live = false; clearTimeout(timer); };
  }, [request, pollMs]);

  useEffect(() => {
    if (request.status !== "pending") return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [request.status]);

  const cancel = async () => {
    setLeaving(true);
    try {
      await apiFetch(`/api/join-requests/${request.id}`, { method: "DELETE" });
    } catch {
      // It leaves the owner's screen when it expires anyway.
    }
    onDone(null);
  };

  const status = pending ? "pending" : request.status === "pending" ? "expired" : request.status;
  const copy = {
    pending: {
      title: "Waiting for approval",
      lede: <>Ask the owner of <strong>{request.registerName}</strong> to approve <strong>{request.deviceName}</strong> on their screen.</>,
    },
    approved: { title: "Approved", lede: <>Opening <strong>{request.registerName}</strong>…</> },
    denied: { title: "Not approved", lede: <>The owner of <strong>{request.registerName}</strong> did not approve this device.</> },
    expired: { title: "Request expired", lede: <>The owner did not answer in time. You can ask again while they are looking at their screen.</> },
    cancelled: { title: "Request closed", lede: <>This request was withdrawn, or the invite was revoked.</> },
  }[status];
  const left = Math.max(0, Math.ceil((request.expiresAt - now) / 1000));

  return (
    <div className="wc-auth">
      <header className="wc-auth__brand">
        <p className="wc-auth__eyebrow">WebCashier</p>
        <h1 className="wc-auth__title">{copy.title}</h1>
        <p className="wc-auth__lede">{copy.lede}</p>
      </header>
      <div className="wc-auth__card">
        {status === "pending" ? (
          <>
            <p className="wc-auth__hint" role="status">This request expires in {formatCountdown(left)}.</p>
            <p className="wc-auth__hint">The owner sees the email you signed in with and this device's name.</p>
            <BigButton variant="ghost" block disabled={leaving} onClick={() => void cancel()}>
              {leaving ? "Cancelling…" : "Cancel request"}
            </BigButton>
          </>
        ) : status === "expired" || status === "cancelled" ? (
          <>
            {error ? <p className="wc-auth__notice wc-auth__notice--error" role="alert">{error}</p> : null}
            <BigButton variant="primary" size="lg" block disabled={busy} onClick={onAskAgain}>
              {busy ? "Asking…" : "Ask again"}
            </BigButton>
            <BigButton variant="ghost" block disabled={busy} onClick={() => onDone(null)}>Not now</BigButton>
          </>
        ) : (
          <BigButton variant="primary" size="lg" block onClick={() => onDone(null)}>Done</BigButton>
        )}
      </div>
    </div>
  );
}

function guessDeviceName(): string {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  if (/iPad/.test(ua)) return "iPad";
  if (/iPhone/.test(ua)) return "iPhone";
  if (/Android/.test(ua)) return "Android device";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows PC";
  return "New device";
}
